import { useEffect, useMemo, useState } from "react";
import { Button, Form, Alert, Badge } from "react-bootstrap";
import { createView, fetchViews } from "../../utilities/AdminUtilities";

const ViewManagement = () => {
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [views, setViews] = useState([]);
  const [selectedView, setSelectedView] = useState(null);
  const [filter, setFilter] = useState("");
  const [copied, setCopied] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateSQL(query)) {
      setError("");
      setLoading(true);
      createView({
        query,
        setLoading,
        setSuccessMessage,
        setError,
      });
    } else {
      setError(
        "Please enter a valid SQL query. Only CREATE VIEW or UPDATE VIEW statements are allowed."
      );
    }
  };

  useEffect(() => {
    fetchViews({ setViews });
  }, [successMessage]);

  const validateSQL = (query) => {
    const sqlRegex = /^(CREATE VIEW|UPDATE VIEW)\s/i;
    return sqlRegex.test(query.trim());
  };

  const handleViewClick = (view) => {
    setSelectedView(view);
  };

  const filteredViews = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return views;
    return views.filter((v) =>
      `${v.schema_name}.${v.view_name}`.toLowerCase().includes(q)
    );
  }, [views, filter]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(selectedView?.definition || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Clipboard can be unavailable (insecure context); nothing else to do.
    }
  };

  return (
    <div className="vm-wrap">
      <div className="vm-card">
        <div className="pv-section-title">Create or Update a View</div>
        <Form onSubmit={handleSubmit}>
          <Form.Control
            as="textarea"
            id="query"
            className="ep-prompt"
            style={{ minHeight: 130 }}
            placeholder="CREATE VIEW schema.view_name AS SELECT ..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            spellCheck={false}
            aria-label="SQL query"
          />
          {error && (
            <Alert variant="danger" className="mt-3 mb-0 py-2">
              {error}
            </Alert>
          )}
          {successMessage && (
            <Alert
              variant="success"
              className="mt-3 mb-0 py-2"
              dismissible
              onClose={() => setSuccessMessage(null)}
            >
              {successMessage}
            </Alert>
          )}
          <div className="vm-submit-row">
            <span className="vm-hint">
              Only <code>CREATE VIEW</code> or <code>UPDATE VIEW</code>{" "}
              statements are accepted.
            </span>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? "Submitting..." : "Run Query"}
            </Button>
          </div>
        </Form>
      </div>

      <div className="vm-split">
        <div className="vm-list-pane">
          <div className="vm-list-head">
            <div className="pv-section-title mb-0">
              Views <span className="vm-count">{filteredViews.length}</span>
            </div>
            <Form.Control
              size="sm"
              placeholder="Filter views"
              aria-label="Filter views"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <div className="vm-list">
            {filteredViews.length === 0 ? (
              <div className="up-empty">No views found.</div>
            ) : (
              filteredViews.map((view, index) => (
                <button
                  type="button"
                  key={`${view.schema_name}.${view.view_name}-${index}`}
                  className={`vm-item${selectedView === view ? " is-selected" : ""}`}
                  onClick={() => handleViewClick(view)}
                >
                  <span className="vm-item-name">{view.view_name}</span>
                  <Badge bg="secondary" pill>
                    {view.schema_name}
                  </Badge>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="vm-detail-pane">
          {selectedView ? (
            <>
              <div className="vm-detail-head">
                <div className="min-w-0">
                  <div className="pv-eyebrow">{selectedView.schema_name}</div>
                  <div className="pv-title up-title text-break">
                    {selectedView.view_name}
                  </div>
                </div>
                <Button size="sm" variant="outline-secondary" onClick={handleCopy}>
                  {copied ? "Copied" : "Copy SQL"}
                </Button>
              </div>
              <pre className="pv-prompt vm-definition">
                {selectedView.definition || "—"}
              </pre>
            </>
          ) : (
            <div className="up-empty vm-placeholder">
              Select a view to see its definition.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewManagement;
